import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  StepStatus,
  IProjectAsset,
  AssetType,
  ScriptCharacter,
  ScriptProp,
  ScriptScene,
  CharactersData,
  PropsData,
  ScenesData,
} from '@packages/global';
import {
  StorySummaryStep,
  VisualStyleStep,
  CharactersStep,
  PropsStep,
  ScenesStep,
  ShotsStep
} from '../steps';
import type { Step1Progress, ShotsGeneratingProgress } from '../../../hooks/scriptGeneration/useScriptPageState';

interface StepContentRendererProps {
  currentStep: number;
  currentStepData: any;
  currentStepStatus: StepStatus | undefined;
  currentStepName: string | undefined;
  scriptId: string | undefined;
  script: any;
  episode: any;
  isEditing: boolean;
  isStep1Generating: boolean;
  step1Progress: Step1Progress;
  shotsGeneratingProgress: ShotsGeneratingProgress;
  newShotIndices: number[];
  onClearNewShotIndices: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onRegenerate: () => void;
  onChange: (data: any) => void;
  onUpdate: () => void;
  /** 来自 ProjectAsset 的主体列表（含本地覆盖），用于实时反映生成状态和图片 */
  episodeAssets?: IProjectAsset[];
}

function episodeAssetsToScriptItems(episodeAssets: IProjectAsset[]) {
  return {
    characters: episodeAssets
      .filter(a => a.assetType === AssetType.CHARACTER)
      .map(a => ({
        name: a.name,
        description: a.description || '',
        prompt: a.prompt || '',
        imageUrl: a.conceptImageUrl || a.frontViewUrl,
        audioUrl: a.voiceReference,
        voiceId: a.voiceModelId,
      })) as ScriptCharacter[],
    props: episodeAssets
      .filter(a => a.assetType === AssetType.PROP)
      .map(a => ({
        name: a.name,
        description: a.description || '',
        prompt: a.prompt || '',
        imageUrl: a.conceptImageUrl || a.frontViewUrl,
      })) as ScriptProp[],
    scenes: episodeAssets
      .filter(a => a.assetType === AssetType.SCENE)
      .map(a => ({
        name: a.name,
        description: a.description || '',
        prompt: a.prompt || '',
        imageUrl: a.conceptImageUrl || a.frontViewUrl,
        location: a.location,
      })) as ScriptScene[],
  };
}

export default function StepContentRenderer({
  currentStep, currentStepData, currentStepStatus, currentStepName,
  scriptId, script, episode,
  isEditing, isStep1Generating, step1Progress,
  shotsGeneratingProgress, newShotIndices, onClearNewShotIndices,
  onEdit, onCancelEdit, onSave, onRegenerate, onChange, onUpdate,
  episodeAssets = [],
}: StepContentRendererProps) {
  const { t } = useTranslation('script');
  const isInProgress = currentStepStatus === StepStatus.IN_PROGRESS;

  if (currentStep === 1) {
    // 步骤已完成时不显示 loading，防止页面刷新后状态不同步
    const step1IsGenerating = isStep1Generating && currentStepStatus !== StepStatus.COMPLETED;
    return (
      <StorySummaryStep
        data={currentStepData}
        isEditing={isEditing}
        isLoading={false}
        isGenerating={step1IsGenerating}
        streamingContent={step1IsGenerating ? {
          title: step1Progress.title,
          welcomeMessage: step1Progress.welcomeMessage,
          summary: step1Progress.summary,
          currentField: step1Progress.field as 'title' | 'welcomeMessage' | 'summary' | undefined,
        } : undefined}
        onEdit={onEdit}
        onCancelEdit={onCancelEdit}
        onSave={onSave}
        onRegenerate={onRegenerate}
        onChange={onChange}
      />
    );
  }

  if (!currentStepData) {
    if (isInProgress) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
          <div className="text-sm">{t('stepContent.generating', { name: currentStepName || '' })}</div>
        </div>
      );
    }
    return <div className="flex items-center justify-center h-full text-gray-400">{t('stepContent.noData')}</div>;
  }

  // 有数据时，在顶部显示生成中提示条（不阻塞内容显示）
  const generatingBanner = isInProgress ? (
    <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-bg-elevated border border-primary/30 text-sm text-primary">
      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary flex-shrink-0" />
      <span>{t('stepContent.generatingUpdating', { name: currentStepName || '' })}</span>
    </div>
  ) : null;

  switch (currentStep) {
    case 2:
      return (
        <>
          {generatingBanner}
          <VisualStyleStep
            data={currentStepData}
            isEditing={isEditing}
            isLoading={false}
            onEdit={onEdit}
            onCancelEdit={onCancelEdit}
            onSave={onSave}
            onRegenerate={onRegenerate}
            onChange={onChange}
          />
        </>
      );
    case 3: {
      // Step 3/4/5：以 ProjectAsset 为主数据源，
      // 脚本 JSON 字段已废弃且可能为空。
      const scriptItems = episodeAssetsToScriptItems(episodeAssets);
      const effectiveData: CharactersData = scriptItems.characters.length > 0
        ? { characters: scriptItems.characters }
        : (currentStepData as CharactersData | null) || { characters: [] };
      return (
        <>
          {generatingBanner}
          <CharactersStep
            data={effectiveData}
            scriptId={scriptId}
            projectId={episode?.projectId}
            episodeId={episode?.id}
            narratorVoiceUrl={script?.narratorVoiceUrl}
            bgmUrl={script?.bgmUrl}
            artStyle={script?.basePrompt}
            isLoading={false}
            imageGenerating={isInProgress}
            episodeAssets={episodeAssets}
            onRegenerate={onRegenerate}
            onUpdate={onUpdate}
          />
        </>
      );
    }
    case 4: {
      const scriptItems = episodeAssetsToScriptItems(episodeAssets);
      const effectiveData: PropsData = scriptItems.props.length > 0
        ? { props: scriptItems.props }
        : (currentStepData as PropsData | null) || { props: [] };
      return (
        <>
          {generatingBanner}
          <PropsStep
            data={effectiveData}
            scriptId={scriptId}
            projectId={episode?.projectId}
            episodeId={episode?.id}
            artStyle={script?.basePrompt}
            isLoading={false}
            imageGenerating={isInProgress}
            episodeAssets={episodeAssets}
            onRegenerate={onRegenerate}
            onUpdate={onUpdate}
          />
        </>
      );
    }
    case 5: {
      const scriptItems = episodeAssetsToScriptItems(episodeAssets);
      const effectiveData: ScenesData = scriptItems.scenes.length > 0
        ? { scenes: scriptItems.scenes }
        : (currentStepData as ScenesData | null) || { scenes: [] };
      return (
        <>
          {generatingBanner}
          <ScenesStep
            data={effectiveData}
            scriptId={scriptId}
            projectId={episode?.projectId}
            episodeId={episode?.id}
            artStyle={script?.basePrompt}
            isLoading={false}
            imageGenerating={isInProgress}
            episodeAssets={episodeAssets}
            onRegenerate={onRegenerate}
            onUpdate={onUpdate}
          />
        </>
      );
    }
    case 6: {
      const scriptItems = episodeAssetsToScriptItems(episodeAssets);
      return (
        <ShotsStep
          data={currentStepData}
          scriptId={scriptId}
          characters={scriptItems.characters}
          props={scriptItems.props}
          scenes={scriptItems.scenes}
          isGenerating={shotsGeneratingProgress.isGenerating || isInProgress}
          generatingProgress={shotsGeneratingProgress}
          newShotIndices={newShotIndices}
          onClearNewShotIndices={onClearNewShotIndices}
          onRegenerate={onRegenerate}
          onUpdate={onUpdate}
        />
      );
    }
    default:
      return null;
  }
}
